import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Heading,
  Badge,
  Spinner,
  Icon,
  Collapse,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { ChevronUpIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { FaBox, FaMapMarkerAlt, FaBolt, FaClock } from "react-icons/fa";
import {
  getDispatchOrdersForSupplier,
  mapOrderToComponentFormat,
} from "../services";
import { IoMdPricetag } from "react-icons/io";
import { RiStackLine } from "react-icons/ri";
import {
  findLatestETAWithGivenTime,
  formatETADateTime,
  sortByDate,
} from "../../../utils/dateResolvers";
import { buildAddress } from "../../../utils/util";
import { GaraazTextComponent } from "@svan-garaaz/reactsharedlib";
import { OrdersCard } from "./OrdersCard";
import { FASTEST_CPT_SLAB_LEVEL } from "../utils";
import EngagementModeBadge from "../../../components/EngagementModeBadge";
import { GroupingMode } from "../types";
import type { EntityData, OrderData, NestedEntityCardProps } from "../types";

export default function NestedEntityCard({
  entity,
  entityType,
  parentEntityId,
  parentEntityName,
  orders: initialOrders,
  expanded,
  onToggleExpansion,
  isLoading = false,
  selectedOrders = [],
  onOrderSelectionChange,
  activeTab = FASTEST_CPT_SLAB_LEVEL,
  collapseSignal,
  showRiderDetail = false,
}: NestedEntityCardProps) {
  const toast = useToast();
  const [orders, setOrders] = useState<OrderData[]>(initialOrders);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const isSupplierEntity = entityType === GroupingMode.SUPPLIER;
  const customerId = isSupplierEntity ? parentEntityId : entity._id;
  const supplierId = isSupplierEntity ? entity._id : parentEntityId;

  // Get display values
  const entityName = entity.businessName || entity.name || "Unknown";
  const orderCount = entity.count || entity.orderCount || 0;
  const urgentCount = entity.urgentOrdersCount || entity.urgentCount || 0;
  const totalItems = entity.totalItemsCount || entity.totalItems || 0;
  const totalQuantity = entity.totalOrderItemsQuantity || entity.totalQuantity || 0;
  const totalValue = entity.totalInvoiceAmount || entity.totalValue || 0;

  // Get the correct address based on entity type
  const entityAddress = useMemo(() => {
    const address = isSupplierEntity
      ? entity.supplierAddress || entity.address
      : entity.shipmentAddress || entity.address;
    
    if (!address?.addressLine1) return "";

    return buildAddress({
      firstLine: address.addressLine1 || "",
      secondLine: address.addressLine2 || "",
      city: address.city || "",
      state: address.state || "",
      pincode: "",
    });
  }, [entity, isSupplierEntity]);

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a, b) => {
      const etaA = findLatestETAWithGivenTime(a?.orderETA ?? []);
      const etaB = findLatestETAWithGivenTime(b?.orderETA ?? []);

      return sortByDate(
        a,
        b,
        () => etaA?.latestTime,
        () => etaB?.latestTime
      );
    });
  }, [orders]);

  const fetchOrders = useCallback(async () => {
    if (!customerId || !supplierId || orders.length > 0) return;

    setOrdersLoading(true);
    try {
      const response = await getDispatchOrdersForSupplier(
        customerId,
        supplierId,
        activeTab
      );
      const apiOrders = response.orders || [];

      const mappedOrders: OrderData[] = apiOrders.map(
        mapOrderToComponentFormat
      );

      setOrders(mappedOrders);
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errorMsg ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        `Failed to fetch orders for this ${entityType}`;

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setOrdersLoading(false);
    }
  }, [customerId, supplierId, orders.length, toast, activeTab, entityType]);

  useEffect(() => {
    if (expanded && orders.length === 0) {
      fetchOrders();
    }
  }, [expanded, orders.length, fetchOrders]);

  useEffect(() => {
    if (collapseSignal && collapseSignal > 0) {
      setOrders([]);
    }
  }, [collapseSignal]);

  // For OrdersCard, we need to provide supplier data
  const supplierForOrdersCard: EntityData = isSupplierEntity
    ? {
        _id: entity._id,
        name: entity.businessName || entity.name || "Unknown Supplier",
        businessName: entity.businessName || entity.name || "Unknown Supplier",
        totalItems: 0,
        totalQuantity: 0,
        totalValue: 0,
        orderCount: 0,
        urgentCount: 0,
      }
    : {
        _id: parentEntityId,
        name: parentEntityName || "Unknown Supplier",
        businessName: parentEntityName || "Unknown Supplier",
        totalItems: 0,
        totalQuantity: 0,
        totalValue: 0,
        orderCount: 0,
        urgentCount: 0,
      };

  // Color schemes based on entity type
  const bgGradient = isSupplierEntity
    ? "linear-gradient(135deg, #f8f7ff 0%, #f3f1ff 100%)"
    : "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)";
  const accentColor = isSupplierEntity ? "purple.500" : "blue.500";

  return (
    <Box
      bg="white"
      borderRadius="xl"
      shadow="0 2px 8px rgba(0, 0, 0, 0.06)"
      border="1px"
      borderColor="gray.100"
      overflow="hidden"
      transition="all 0.2s"
      _hover={{ shadow: "0 4px 16px rgba(0, 0, 0, 0.1)" }}
    >
      <Box
        bg={bgGradient}
        p={4}
        position="relative"
        overflow="hidden"
        onClick={onToggleExpansion}
        cursor={"pointer"}
      >
        <Box
          position="absolute"
          top="-60px"
          right="-60px"
          w="150px"
          h="150px"
          opacity={0.02}
          bg="linear-gradient(135deg, #64748b 0%, #475569 100%)"
          borderRadius="full"
          transform="translate(50px, -50px)"
        />

        <VStack align="stretch" spacing={4} position="relative" zIndex={1}>
          <HStack justify="space-between" align="flex-start">
            <VStack align="start" spacing={2} flex={1}>
              <VStack align="start" spacing={1.5}>
                <HStack spacing={2} align="center">
                  <Heading size="md" fontWeight="600" color="gray.800">
                    {entityName}
                  </Heading>
                  <HStack spacing={2}>
                    <Badge
                      bg="white"
                      color="gray.700"
                      variant="subtle"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      fontWeight="600"
                      shadow="0 1px 3px rgba(0, 0, 0, 0.06)"
                      border="1px"
                      borderColor="gray.200"
                      fontSize="xs"
                      lineHeight={"normal"}
                      textTransform={"none"}
                    >
                      {orderCount} Orders
                    </Badge>
                    {urgentCount > 0 && (
                      <Badge
                        bg="orange.50"
                        color="orange.700"
                        variant="subtle"
                        px={3}
                        py={0.5}
                        borderRadius="full"
                        fontWeight="600"
                        shadow="0 2px 4px rgba(0, 0, 0, 0.06)"
                        border="1px"
                        borderColor="orange.200"
                        lineHeight={"normal"}
                        textTransform={"none"}
                      >
                        <HStack spacing={1.5}>
                          <Icon as={FaBolt} boxSize={3} color="orange.500" />
                          <Text fontSize="xs" fontWeight="600">
                            {urgentCount} Urgent
                          </Text>
                        </HStack>
                      </Badge>
                    )}
                    {entity.engagementMode && (
                      <EngagementModeBadge
                        engagementMode={entity.engagementMode}
                        size="sm"
                      />
                    )}
                  </HStack>
                </HStack>
                {entityAddress && (
                  <HStack spacing={2} color="gray.600">
                    <Icon
                      as={FaMapMarkerAlt}
                      boxSize={2.5}
                      color={accentColor}
                    />
                    <GaraazTextComponent
                      header=""
                      content={entityAddress}
                      maxNoOfLines={3}
                      contentStyle={{
                        width: "400px",
                        fontSize: "12px",
                        fontWeight: "500",
                        color: "gray.700",
                        cursor: "pointer",
                      }}
                    />
                  </HStack>
                )}
              </VStack>
            </VStack>

            <HStack spacing={4} align="center">
              <HStack spacing={4}>
                <VStack spacing={1} align="center" width={"200px"}>
                  <HStack spacing={1.5} align="center">
                    <Icon as={FaClock} color="orange.500" boxSize={3} />
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      Customer Promise Time
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="700" color="gray.800">
                    {formatETADateTime(entity.minETAdate)}
                  </Text>
                </VStack>

                <Box w="1px" h="6" bg="gray.200" />

                <VStack spacing={1} align="center" width={"80px"}>
                  <HStack spacing={1.5} align="center">
                    <Icon as={FaBox} color="blue.500" boxSize={3} />
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      Items
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="700" color="gray.800">
                    {totalItems}
                  </Text>
                </VStack>

                <Box w="1px" h="6" bg="gray.200" />

                <VStack spacing={1} align="center" width={"80px"}>
                  <HStack spacing={1.5} align="center">
                    <Icon as={RiStackLine} color="green.500" boxSize={3} />
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      Qty
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="700" color="gray.800">
                    {totalQuantity}
                  </Text>
                </VStack>

                <Box w="1px" h="6" bg="gray.200" />

                <VStack spacing={1} align="center" width={"80px"}>
                  <HStack spacing={1.5} align="center">
                    <Icon as={IoMdPricetag} color="purple.500" boxSize={3} />
                    <Text fontSize="xs" color="gray.600" fontWeight="600">
                      Value
                    </Text>
                  </HStack>
                  <Text fontSize="lg" fontWeight="700" color="gray.800">
                    ₹{(typeof totalValue === 'number' ? totalValue : parseFloat(String(totalValue || 0))).toLocaleString()}
                  </Text>
                </VStack>
              </HStack>

              <IconButton
                aria-label="Toggle expansion"
                icon={
                  expanded ? (
                    <ChevronUpIcon boxSize={8} />
                  ) : (
                    <ChevronDownIcon boxSize={8} />
                  )
                }
                _hover={{
                  bg: "none",
                }}
                variant="ghost"
                size="lg"
              />
            </HStack>
          </HStack>

          {isLoading && (
            <HStack
              spacing={2}
              justify="center"
              bg="white"
              p={3}
              borderRadius="lg"
              shadow="0 1px 4px rgba(0, 0, 0, 0.06)"
            >
              <Spinner size="sm" color="blue.500" />
              <Text fontSize="xs" color="gray.600" fontWeight="500">
                Loading orders...
              </Text>
            </HStack>
          )}
        </VStack>
      </Box>

      <Collapse in={expanded}>
        <OrdersCard
          sortedOrders={sortedOrders}
          onOrderSelectionChange={onOrderSelectionChange}
          selectedOrders={selectedOrders}
          supplier={supplierForOrdersCard}
          customerId={customerId}
          ordersLoading={ordersLoading}
          orders={orders}
          showRiderDetail={showRiderDetail}
        />
      </Collapse>
    </Box>
  );
}

// Export for backward compatibility
export { NestedEntityCard as SupplierCard };

